import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiLogOut, FiHome, FiDollarSign, FiUsers,
  FiTrendingUp, FiSettings, FiUser, FiFileText, FiBriefcase,
  FiCopy, FiCheck, FiSend, FiChevronLeft, FiChevronRight, FiChevronDown, FiAward,
  FiShield, FiShoppingBag, FiShoppingCart
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import {
  Users,
  Shield,
  TrendingUp,
  HandCoins,
  Layers,
  Users2,
  UserCheck,
  PlaneTakeoff,
  Car,
  Crown,
  FileSpreadsheet,
  Wallet as WalletIcon,
  Receipt,
  CreditCard,
  Gavel
} from "lucide-react";
import { useCart } from "../context/CartContext";
import MemberDashboard from "./dashboards/MemberDashboard";
import LBBPDashboard from "./dashboards/CSPDashboard";
import PUAPDashboard from "./dashboards/PUAPDashboard";
import { OfficeManagerDashboard, MPCDashboard } from "./dashboards/RoleDashboards";
import MobileStoreDashboard from "./MobileStoreDashboard";
import Wallet from "./Wallet";
import Team from "./Team";
import BuyShare from "./BuyShare";
import AdminDashboard from "./AdminDashboard";
import BuyProduct from "./BuyProduct";
import SharePurchases from "./SharePurchases";
import LevelIncomeReport from "./LevelIncomeReport";
import UserProfilePage from "./UserProfilePage";
import RegisterKYCDashboard from "./RegisterKYC";
import RegisterPartnerDashboard from "./RegisterPartner";
import ManualPayment from "./ManualPayment";
import UserTransactions from "./UserTransactions";
import RoyaltyHistory from "./RoyaltyHistory";
import PUAPIncomePage from "./PUAPIncomePage";
import { WelcomeLetterPage, IdCardPage } from "./MemberDocuments";
import RoyaltyEligibility from "./RoyaltyEligibility";

import ProductHome from "../components/ProductHome";
import logo from "../assets/logo.png";
import DirectTeam from "./DirectTeam";
import MatrixTeam from "./MatrixTeam";
import GenerationTeam from "./GenerationTeam";
import SelfPurchase from "./SelfPurchase";
import MyOrders from "./MyOrders";
import PurchaseVolume from "./PurchaseVolume";
import CategoryProductsPage from "./CategoryProductsPage";

/* ─────────────────────────────────────────────────────
   DESIGN TOKENS
   Desktop : unchanged navy/amber luxury
   Mobile  : Option B — Teal + Coral Modern
───────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  :root {
    --navy-950: #050d1a;
    --navy-900: #0a1628;
    --navy-800: #0f2040;
    --navy-700: #162a52;
    --navy-600: #1d3565;
    --amber-400: #fbbf24;
    --amber-300: #fcd34d;
    --amber-500: #f59e0b;
    --slate-100: #f1f5f9;
    --slate-200: #e2e8f0;
    --slate-400: #94a3b8;
    --slate-500: #64748b;
    --slate-600: #475569;
    --slate-700: #334155;
    --slate-800: #1e293b;
    --white: #ffffff;
    --sidebar-w: 260px;
    --sidebar-collapsed: 72px;
    --header-h: 64px;
    --mobile-bottom-nav-h: 68px;
    --radius-sm: 8px;
    --radius-md: 12px;
    --radius-lg: 18px;
    --radius-xl: 24px;
    --shadow-sm: 0 1px 3px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.06);
    --shadow-md: 0 4px 16px rgba(0,0,0,.10);
    --shadow-lg: 0 8px 32px rgba(0,0,0,.14);
    --transition: 230ms cubic-bezier(.4,0,.2,1);
    --teal-600: #0d9488;
    --teal-500: #14b8a6;
    --teal-50:  #f0fdfa;
    --coral:    #f97316;
  }

  .abm-root, .abm-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .abm-root { font-family: 'Montserrat', sans-serif; background: var(--slate-100); }
  .abm-root ::-webkit-scrollbar { width: 4px; height: 4px; }
  .abm-root ::-webkit-scrollbar-track { background: transparent; }
  .abm-root ::-webkit-scrollbar-thumb { background: var(--slate-400); border-radius: 99px; }
  .abm-shell { display: flex; height: 100dvh; overflow: hidden; background: var(--slate-100); }

  /* ================================================
     DESKTOP SIDEBAR  (unchanged)
  ================================================ */
  .abm-sidebar {
    position: fixed; left: 0; top: 0; bottom: 0; z-index: 40;
    width: var(--sidebar-w); background: var(--navy-950);
    display: flex; flex-direction: column;
    transition: transform var(--transition), width var(--transition);
    overflow: hidden;
  }
  .abm-sidebar.collapsed { width: var(--sidebar-collapsed); }
  .abm-sidebar.mobile-hidden { transform: translateX(-100%); }
  .abm-sidebar.mobile-visible { transform: translateX(0); }
  .abm-sidebar::before {
    content:''; position:absolute; top:0; right:0; width:1px; height:100%;
    background:linear-gradient(to bottom,transparent,var(--navy-700) 30%,var(--amber-500) 60%,transparent);
    opacity:.5;
  }
  .abm-logo { display:flex; align-items:center; gap:12px; padding:20px 16px 18px; border-bottom:1px solid rgba(255,255,255,.06); flex-shrink:0; }
  .abm-logo-mark { width:38px; height:38px; border-radius:var(--radius-sm); background:linear-gradient(135deg,var(--amber-400),var(--amber-500)); display:flex; align-items:center; justify-content:center; font-family:'Montserrat',sans-serif; font-weight:800; font-size:14px; color:var(--navy-950); flex-shrink:0; box-shadow:0 0 0 3px rgba(251,191,36,.15); }
  .abm-logo-text { font-family:'Montserrat',sans-serif; font-weight:700; font-size:15px; color:var(--white); white-space:nowrap; overflow:hidden; opacity:1; transition:opacity var(--transition); }
  .abm-sidebar.collapsed .abm-logo-text { opacity:0; pointer-events:none; }
  .abm-user-card { margin:12px 10px; padding:12px; border-radius:var(--radius-md); background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.07); flex-shrink:0; }
  .abm-user-card-inner { display:flex; align-items:center; gap:10px; }
  .abm-avatar { width:38px; height:38px; border-radius:10px; flex-shrink:0; background:linear-gradient(135deg,var(--navy-600),var(--navy-700)); border:2px solid var(--amber-500); display:flex; align-items:center; justify-content:center; font-family:'Montserrat',sans-serif; font-weight:700; font-size:13px; color:var(--amber-300); }
  .abm-user-info { min-width:0; flex:1; }
  .abm-user-name { font-weight:600; font-size:13px; color:var(--white); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .abm-user-id { font-size:11px; color:var(--slate-400); margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .abm-sidebar.collapsed .abm-user-card { margin:8px 6px; }
  .abm-sidebar.collapsed .abm-user-info { display:none; }
  .abm-nav { flex:1; overflow-y:auto; padding:8px 10px; }
  .abm-nav-section { margin-bottom:6px; }
  .abm-nav-label { font-size:10px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:var(--slate-500); padding:8px 8px 4px; white-space:nowrap; overflow:hidden; transition:opacity var(--transition); cursor:pointer; display:flex; align-items:center; gap:6px; user-select:none; }
  .abm-nav-label:hover { color:var(--slate-400); }
  .abm-nav-label-icon { flex-shrink:0; width:12px; height:12px; transition:transform var(--transition); }
  .abm-nav-section.collapsed .abm-nav-label-icon { transform:rotate(-90deg); }
  .abm-sidebar.collapsed .abm-nav-label { opacity:0; pointer-events:none; }
  .abm-nav-items { overflow:hidden; transition:max-height var(--transition), opacity var(--transition); max-height:550px; opacity:1; }
  .abm-nav-section.collapsed .abm-nav-items { max-height:0; opacity:0; }
  .abm-nav-btn { width:100%; display:flex; align-items:center; gap:11px; padding:10px; border-radius:var(--radius-sm); border:none; background:transparent; cursor:pointer; color:var(--slate-400); font-family:'Montserrat',sans-serif; font-size:13.5px; font-weight:500; transition:all var(--transition); white-space:nowrap; position:relative; }
  .abm-nav-btn:hover { background:rgba(255,255,255,.06); color:var(--white); }
  .abm-nav-btn.active { background:linear-gradient(135deg,rgba(251,191,36,.15),rgba(245,158,11,.08)); color:var(--amber-300); box-shadow:inset 0 0 0 1px rgba(251,191,36,.2); }
  .abm-nav-btn.active .abm-nav-icon { color:var(--amber-400); }
  .abm-nav-icon { flex-shrink:0; width:18px; height:18px; }
  .abm-nav-label-text { overflow:hidden; opacity:1; white-space:nowrap; transition:opacity var(--transition); }
  .abm-sidebar.collapsed .abm-nav-label-text { opacity:0; pointer-events:none; }
  .abm-nav-btn.active::before { content:''; position:absolute; left:-2px; top:50%; transform:translateY(-50%); width:3px; height:60%; border-radius:99px; background:var(--amber-400); }
  .abm-collapse-btn { margin:8px 10px 12px; display:flex; align-items:center; justify-content:center; gap:8px; padding:8px; border-radius:var(--radius-sm); border:1px solid rgba(255,255,255,.07); background:rgba(255,255,255,.04); cursor:pointer; color:var(--slate-400); font-family:'Montserrat',sans-serif; font-size:12px; font-weight:500; transition:all var(--transition); flex-shrink:0; }
  .abm-collapse-btn:hover { color:var(--white); background:rgba(255,255,255,.08); }
  .abm-collapse-label { white-space:nowrap; overflow:hidden; transition:opacity var(--transition); }
  .abm-sidebar.collapsed .abm-collapse-label { opacity:0; width:0; }
  .abm-logout-btn { margin:0 10px 14px; display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:var(--radius-sm); border:1px solid rgba(239,68,68,.2); background:transparent; cursor:pointer; color:#f87171; font-family:'Montserrat',sans-serif; font-size:13px; font-weight:500; transition:all var(--transition); white-space:nowrap; flex-shrink:0; }
  .abm-logout-btn:hover { background:rgba(239,68,68,.1); border-color:rgba(239,68,68,.4); }
  .abm-logout-label { overflow:hidden; transition:opacity var(--transition); }
  .abm-sidebar.collapsed .abm-logout-label { opacity:0; width:0; }

  .abm-overlay { position:fixed; inset:0; z-index:39; background:rgba(0,0,0,.55); backdrop-filter:blur(2px); opacity:0; pointer-events:none; transition:opacity var(--transition); }
  .abm-overlay.visible { opacity:1; pointer-events:all; }

  .abm-main { flex:1; display:flex; flex-direction:column; min-width:0; overflow:hidden; margin-left:var(--sidebar-w); transition:margin-left var(--transition); }
  .abm-main.sidebar-collapsed { margin-left:var(--sidebar-collapsed); }
  .abm-main.mobile { margin-left:0; }

  .abm-header { height:var(--header-h); flex-shrink:0; background:var(--white); border-bottom:1px solid var(--slate-200); display:flex; align-items:center; gap:12px; padding:0 20px; box-shadow:var(--shadow-sm); position:sticky; top:0; z-index:20; }
  .abm-header-title { flex:1; min-width:0; }
  .abm-header-breadcrumb { font-size:11px; color:var(--slate-400); font-weight:500; text-transform:uppercase; letter-spacing:.06em; }
  .abm-header-page { font-family:'Montserrat',sans-serif; font-weight:700; font-size:16px; color:var(--slate-800); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .abm-header-user { display:flex; align-items:center; gap:8px; padding:6px 10px; border-radius:var(--radius-md); background:var(--slate-100); border:1px solid var(--slate-200); }
  .abm-header-avatar { width:28px; height:28px; border-radius:7px; flex-shrink:0; background:var(--navy-800); border:1.5px solid var(--amber-400); display:flex; align-items:center; justify-content:center; font-family:'Montserrat',sans-serif; font-weight:700; font-size:10px; color:var(--amber-300); }
  .abm-header-user-info { min-width:0; }
  .abm-header-user-name { font-size:12px; font-weight:600; color:var(--slate-800); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:120px; }
  .abm-header-user-id { font-size:10px; color:var(--slate-500); }

  .abm-content { flex:1; overflow-y:auto; padding:24px; }
  @media (max-width: 768px) { .abm-content { padding:0 0 calc(var(--mobile-bottom-nav-h) + 8px) 0; } }

  .abm-ref-card { background:var(--white); border:1px solid var(--slate-200); border-radius:var(--radius-lg); padding:18px 20px; margin-bottom:22px; box-shadow:var(--shadow-sm); display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
  @media (max-width: 768px) { .abm-ref-card { display:none; } }
  .abm-ref-icon { width:44px; height:44px; border-radius:12px; flex-shrink:0; background:linear-gradient(135deg,var(--navy-800),var(--navy-700)); display:flex; align-items:center; justify-content:center; color:var(--amber-400); }
  .abm-ref-body { flex:1; min-width:0; }
  .abm-ref-label { font-size:11px; font-weight:600; color:var(--slate-500); text-transform:uppercase; letter-spacing:.06em; }
  .abm-ref-id { font-family:'Montserrat',sans-serif; font-size:17px; font-weight:700; color:var(--slate-800); }
  .abm-ref-name { font-size:12.5px; color:var(--slate-600); margin-top:1px; }
  .abm-ref-link { font-size:11px; color:var(--slate-400); margin-top:3px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:320px; }
  .abm-copy-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border-radius:var(--radius-sm); border:none; cursor:pointer; flex-shrink:0; font-family:'Montserrat',sans-serif; font-size:13px; font-weight:600; background:linear-gradient(135deg,var(--navy-800),var(--navy-900)); color:var(--amber-300); box-shadow:0 2px 8px rgba(0,0,0,.15); transition:all var(--transition); }
  .abm-copy-btn:hover { transform:translateY(-1px); }
  .abm-copy-btn.copied { background:linear-gradient(135deg,#065f46,#047857); color:#6ee7b7; }

  .abm-loading { min-height:100dvh; display:flex; align-items:center; justify-content:center; background:var(--navy-950); font-family:'Montserrat',sans-serif; }
  .abm-spinner { width:48px; height:48px; border-radius:50%; border:3px solid rgba(251,191,36,.2); border-top-color:var(--amber-400); animation:spin 0.8s linear infinite; margin:0 auto 16px; }
  @keyframes spin { to { transform:rotate(360deg); } }
  .abm-loading-text { color:var(--slate-400); font-size:14px; font-weight:500; text-align:center; }

  .abm-error { min-height:100dvh; display:flex; align-items:center; justify-content:center; background:var(--slate-100); padding:24px; }
  .abm-error-card { background:var(--white); border-radius:var(--radius-xl); padding:40px 32px; max-width:380px; width:100%; box-shadow:var(--shadow-lg); text-align:center; }
  .abm-error-emoji { font-size:52px; margin-bottom:16px; }
  .abm-error-title { font-family:'Montserrat',sans-serif; font-weight:700; font-size:20px; color:var(--slate-800); margin-bottom:8px; }
  .abm-error-sub { font-size:14px; color:var(--slate-500); margin-bottom:24px; }
  .abm-error-btn { width:100%; padding:13px; background:linear-gradient(135deg,var(--navy-800),var(--navy-900)); color:var(--amber-300); border:none; border-radius:var(--radius-md); font-family:'Montserrat',sans-serif; font-weight:700; font-size:14px; cursor:pointer; transition:all var(--transition); }

  .abm-dual-header { background:var(--white); border-radius:var(--radius-lg); padding:20px 24px; margin-bottom:18px; box-shadow:var(--shadow-sm); border:1px solid var(--slate-200); }
  .abm-dual-title { font-family:'Montserrat',sans-serif; font-weight:800; font-size:clamp(18px,4vw,28px); color:var(--slate-800); margin-bottom:4px; }
  .abm-dual-sub { font-size:13.5px; color:var(--slate-500); }
  .abm-tab-row { background:var(--white); border-radius:var(--radius-lg); padding:6px; margin-bottom:18px; box-shadow:var(--shadow-sm); border:1px solid var(--slate-200); display:flex; gap:6px; }
  @media (max-width: 480px) { .abm-tab-row { flex-direction:column; } }
  .abm-tab-btn { flex:1; padding:11px 16px; border-radius:var(--radius-sm); border:none; cursor:pointer; font-family:'Montserrat',sans-serif; font-size:13.5px; font-weight:600; transition:all var(--transition); color:var(--slate-600); background:transparent; }
  .abm-tab-btn:hover { background:var(--slate-100); color:var(--slate-800); }
  .abm-tab-btn.active { background:linear-gradient(135deg,var(--navy-800),var(--navy-900)); color:var(--amber-300); box-shadow:0 2px 10px rgba(0,0,0,.15); }
  .abm-tab-content { background:var(--white); border-radius:var(--radius-lg); box-shadow:var(--shadow-sm); border:1px solid var(--slate-200); }

  /* ================================================
     MOBILE — Option B: Teal + Coral Modern
  ================================================ */

  .abm-mobile-header {
    background: var(--teal-600);
    flex-shrink: 0;
  }

  .abm-mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 10px;
  }

  .abm-mobile-logo {
    width: 80px; height: 80px; border-radius: 9px;
    background: #05F1FF;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 12px;
    color: var(--teal-600);
    box-shadow: 0 2px 8px rgba(0,0,0,.12);
    flex-shrink: 0;
  }

  .abm-mobile-userinfo { text-align: center; flex: 1; padding: 0 10px; }
  .abm-mobile-username { font-size: 12px; font-weight: 600; color: #fff; }
  .abm-mobile-userid { font-size: 10px; color: rgba(255,255,255,.72); margin-top: 1px; }

  .abm-mobile-avatar {
    width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
    background: rgba(255,255,255,.18);
    border: 2px solid rgba(255,255,255,.38);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 13px;
    color: #fff; cursor: pointer;
    transition: background var(--transition);
  }
  .abm-mobile-avatar:active { background: rgba(255,255,255,.30); }

  .abm-mobile-balance-row {
    padding: 4px 14px 20px;
  }

  .abm-mobile-balance-pill {
    background: rgba(255,255,255,.15);
    border: 1px solid rgba(255,255,255,.28);
    border-radius: 9px;
    padding: 13px 18px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .abm-mobile-bal-label {
    font-size: 11px;
    font-weight: 800;
    color: white;
    text-transform: uppercase;
    letter-spacing: .07em;
  }

  .abm-mobile-bal-amount {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: #fff;
    margin-top: 0;
    text-align:center;
  }

  .abm-mobile-bal-badge {
    background: rgba(255,255,255,.2);
    border: 1px solid rgba(255,255,255,.32);
    border-radius: 99px;
    padding: 5px 13px;
    display: flex; align-items: center; gap: 5px;
    font-size: 10px; font-weight: 700; color: #fff;
  }
  .abm-mobile-bal-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #4ade80;
    box-shadow: 0 0 0 2px rgba(74,222,128,.35);
    flex-shrink: 0;
  }

  .abm-mobile-action-card {
    background: var(--white);
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.04);
    margin: 12px -8px 0;
    position: relative;
    z-index: 2;
    overflow: hidden;
    border: 1px solid rgba(0,0,0,.05);
  }

  .abm-action-section-label {
    font-size: 11px; font-weight: 800;
    color: var(--slate-700);
    text-transform: uppercase; letter-spacing: .12em;
    padding: 18px 16px 12px;
  }
  @media (max-width: 768px) {
    .abm-action-section-label {
      text-align: center;
      background: linear-gradient(135deg,  #2563eb 100%,  #2563eb 100%, #2563eb 100%);
      color: var(--amber-300);
      padding: 10px 12px;
    }
  }

  .abm-mobile-actions {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0 8px 16px;
    gap: 12px;
  }

  .abm-mobile-action-btn {
    display: flex; flex-direction: column; align-items: center; gap: 10px;
    padding: 12px 8px;
    border: none; background: transparent; cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    transition: all 0.25s cubic-bezier(.4,0,.2,1);
    -webkit-tap-highlight-color: transparent;
    border-radius: 9px;
  }
  .abm-mobile-action-btn:hover {
    transform: translateY(-4px);
  }
  .abm-mobile-action-btn:active {
    transform: translateY(-2px) scale(0.97);
  }
  .abm-mobile-actions-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    text-align: center;
    color: blue;
    font-weight: bold;
    margin-top: 10px;
  }

  .abm-mobile-action-icon {
    width: 72px; height: 72px; border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
  }
  .abm-mobile-action-icon::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 50%);
    border-radius: 9px;
    z-index: 1;
    pointer-events: none;
  }
  .abm-mobile-action-icon::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    right: 2px;
    height: 40%;
    background: linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 100%);
    border-radius: 16px 16px 100% 100%;
    z-index: 2;
    pointer-events: none;
  }

  .abm-mobile-action-label {
    font-size: 11px; font-weight: 800;
    color: var(--slate-800);
    text-align: center; line-height: 1.3;
  }

  .abm-actions-divider {
    height: 1px; background: var(--slate-100);
    margin: 0 14px;
  }

  // .abm-mobile-page-content { padding: 14px 12px; }

  .abm-mobile-bottom-nav {
    display: none;
    position: fixed; bottom: 0; left: 0; right: 0;
    height: var(--mobile-bottom-nav-h);
    background: var(--white);
    border-top: 1px solid var(--slate-200);
    box-shadow: 0 -4px 24px rgba(13,148,136,.07);
    z-index: 50;
    align-items: center;
    justify-content: space-around;
    padding: 0 4px;
  }
  .abm-mobile-nav-item {
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    flex: 1; padding: 8px 2px 4px;
    border: none; background: transparent; cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    transition: all var(--transition);
    position: relative;
    -webkit-tap-highlight-color: transparent;
  }
  .abm-mobile-nav-icon { color: var(--slate-400); transition: color var(--transition); }
  .abm-mobile-nav-label { font-size: 10px; font-weight: 600; color: var(--slate-400); transition: color var(--transition); }
  .abm-mobile-nav-item.active .abm-mobile-nav-icon { color: var(--teal-600); }
  .abm-mobile-nav-item.active .abm-mobile-nav-label { color: var(--teal-600); }
  .abm-mobile-nav-item.active::after {
    content: ''; position: absolute; bottom: 0; left: 22%; right: 22%;
    height: 2.5px; border-radius: 99px; background: var(--teal-600);
  }

  .abm-mobile-nav-fab {
    width: 54px; height: 54px; border-radius: 50%;
    background: var(--coral);
    border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 18px rgba(249,115,22,.42);
    transition: all 200ms cubic-bezier(.4,0,.2,1);
    flex-shrink: 0;
    margin-top: -12px;
    -webkit-tap-highlight-color: transparent;
  }
  .abm-mobile-nav-fab:hover { transform: scale(1.07); box-shadow: 0 6px 22px rgba(249,115,22,.52); }
  .abm-mobile-nav-fab:active { transform: scale(.93); }

  @media (max-width: 768px) {
    .abm-sidebar { display: none !important; }
    .abm-mobile-bottom-nav { display: flex; }
    .abm-header { display: none; }
  }
  @media (min-width: 769px) {
    .abm-sidebar.mobile-hidden { transform: none; }
    .abm-mobile-header { display: none !important; }
    .abm-mobile-bottom-nav { display: none !important; }
    .abm-header { display: flex; }
  }
`;

/* ── Page meta ── */
const PAGE_META = {
  dashboard: { label: "Dashboard", breadcrumb: "Overview" },
  "home-dashboard": { label: "Dashboard", breadcrumb: "Overview" },
  wallet: { label: "Wallet", breadcrumb: "Finance" },
  team: { label: "My Team", breadcrumb: "Network" },
  "level-income": { label: "CCP Level Income", breadcrumb: "Reports" },
  "royalty-history": { label: "GIP Royalty History", breadcrumb: "Reports" },
  "royalty-eligibility": { label: "Royalty Eligibility", breadcrumb: "Reports" },
  "buy-share": { label: "GCP Buy Share", breadcrumb: "Investments" },
  "buy-product": { label: "Buy Product", breadcrumb: "Shopping" },
  "share-purchases": { label: "My GCP Loan History", breadcrumb: "Investments" },
  "complete-kyc": { label: "Complete KYC", breadcrumb: "Verification" },
  "select-partner": { label: "Select Partnership", breadcrumb: "Onboarding" },
  "manual-payment": { label: "Add Money", breadcrumb: "Payments" },
  transactions: { label: "Transactions", breadcrumb: "Finance" },
  profile: { label: "My Profile", breadcrumb: "Account" },
  "welcome-letter": { label: "Welcome Letter", breadcrumb: "Documents" },
  "id-card": { label: "Member ID Card", breadcrumb: "Documents" },
  "my-shop": { label: "My Shop", breadcrumb: "Shopping" },
  account: { label: "Account", breadcrumb: "Account" },
};

/* ── Shared ACTION_COLORS ── */
const ACTION_COLORS = {
  team: { bg: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)", color: "#fff", shadow: "0 4px 12px rgba(139,92,246,0.3)" },
  wallet: { bg: "linear-gradient(135deg, #10B981 0%, #34D399 100%)", color: "#fff", shadow: "0 4px 12px rgba(16,185,129,0.3)" },
  transactions: { bg: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)", color: "#fff", shadow: "0 4px 12px rgba(245,158,11,0.3)" },
  "manual-payment": { bg: "linear-gradient(135deg, #F97316 0%, #FB923C 100%)", color: "#fff", shadow: "0 4px 12px rgba(249,115,22,0.3)" },
  "level-income": { bg: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)", color: "#fff", shadow: "0 4px 12px rgba(239,68,68,0.3)" },
  "royalty-history": { bg: "linear-gradient(135deg, #EC4899 0%, #F472B6 100%)", color: "#fff", shadow: "0 4px 12px rgba(236,72,153,0.3)" },
  "buy-share": { bg: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)", color: "#fff", shadow: "0 4px 12px rgba(59,130,246,0.3)" },
  "share-purchases": { bg: "linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)", color: "#fff", shadow: "0 4px 12px rgba(6,182,212,0.3)" },
  "complete-kyc": { bg: "linear-gradient(135deg, #6366F1 0%, #818CF8 100%)", color: "#fff", shadow: "0 4px 12px rgba(99,102,241,0.3)" },
  "direct-income": { bg: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)", color: "#fff", shadow: "0 4px 12px rgba(139,92,246,0.3)" },
  "matrix-income": { bg: "linear-gradient(135deg, #10B981 0%, #34D399 100%)", color: "#fff", shadow: "0 4px 12px rgba(16,185,129,0.3)" },
  "generation-income": { bg: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)", color: "#fff", shadow: "0 4px 12px rgba(245,158,11,0.3)" },
  "active-member-income": { bg: "linear-gradient(135deg, #F97316 0%, #FB923C 100%)", color: "#fff", shadow: "0 4px 12px rgba(249,115,22,0.3)" },
  "reward-tour-income": { bg: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)", color: "#fff", shadow: "0 4px 12px rgba(239,68,68,0.3)" },
  "car-bonus-income": { bg: "linear-gradient(135deg, #EC4899 0%, #F472B6 100%)", color: "#fff", shadow: "0 4px 12px rgba(236,72,153,0.3)" },
  "royalty-income": { bg: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)", color: "#fff", shadow: "0 4px 12px rgba(59,130,246,0.3)" },
  "direct-team": { bg: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)", color: "#fff", shadow: "0 4px 12px rgba(59,130,246,0.3)" },
  "matrix-team": { bg: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)", color: "#fff", shadow: "0 4px 12px rgba(139,92,246,0.3)" },
  "generation-team": { bg: "linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)", color: "#fff", shadow: "0 4px 12px rgba(6,182,212,0.3)" },
  "self-purchase": { bg: "linear-gradient(135deg, #10B981 0%, #34D399 100%)", color: "#fff", shadow: "0 4px 12px rgba(16,185,129,0.3)" },
  "my-orders": { bg: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)", color: "#fff", shadow: "0 4px 12px rgba(245,158,11,0.3)" },
  "purchase-volume": { bg: "linear-gradient(135deg, #EC4899 0%, #F472B6 100%)", color: "#fff", shadow: "0 4px 12px rgba(236,72,153,0.3)" },
  profile: { bg: "linear-gradient(135deg, #64748B 0%, #94A3B8 100%)", color: "#fff", shadow: "0 4px 12px rgba(100,116,139,0.3)" },
  "my-shop": { bg: "linear-gradient(135deg, #10B981 0%, #34D399 100%)", color: "#fff", shadow: "0 4px 12px rgba(16,185,129,0.3)" },
  "select-partner": { bg: "linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)", color: "#fff", shadow: "0 4px 12px rgba(6,182,212,0.3)" },
  "buy-product": { bg: "linear-gradient(135deg, #10B981 0%, #34D399 100%)", color: "#fff", shadow: "0 4px 12px rgba(16,185,129,0.3)" },
  admin: { bg: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)", color: "#fff", shadow: "0 4px 12px rgba(15,23,42,0.3)" },
  "royalty-eligibility": { bg: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)", color: "#fff", shadow: "0 4px 12px rgba(15,23,42,0.3)" },
  "id-card": { bg: "linear-gradient(135deg, #10B981 0%, #34D399 100%)", color: "#fff", shadow: "0 4px 12px rgba(16,185,129,0.3)" },
  "welcome-letter":  { bg: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)", color: "#fff", shadow: "0 4px 12px rgba(245,158,11,0.3)" },
};

/* ── Desktop NavItem ── */
const NavItem = ({ icon, label, active, sidebarOpen, onClick }) => (
  <button
    onClick={onClick}
    className={`abm-nav-btn ${active ? "active" : ""}`}
    title={!sidebarOpen ? label : undefined}
  >
    <span className="abm-nav-icon">{icon}</span>
    <span className="abm-nav-label-text">{label}</span>
  </button>
);

/* ── Collapsible Nav Section ── */
const CollapsibleNavSection = ({ label, collapsed, onToggle, children, sidebarCollapsed }) => (
  <div className={`abm-nav-section ${collapsed ? "collapsed" : ""}`}>
    <div className="abm-nav-label" onClick={onToggle}>
      <span className="abm-nav-label-icon">
        {collapsed ? <FiChevronRight size={10} /> : <FiChevronDown size={10} />}
      </span>
      {label}
    </div>
    <div className="abm-nav-items">
      {children}
    </div>
  </div>
);

/* ── Shared ActionBtn for mobile ── */
const ActionBtn = ({ page, label, icon, navigate_, onClick, cartCount }) => {
  const c = page === "whatsapp"
    ? { bg: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)", color: "#fff", shadow: "0 4px 12px rgba(22,163,74,0.3)" }
    : ACTION_COLORS[page] || { bg: "linear-gradient(135deg, #64748B 0%, #94A3B8 100%)", color: "#fff", shadow: "0 4px 12px rgba(100,116,139,0.3)" };
  const handleClick = onClick || (() => navigate_(page));
  return (
    <button className="abm-mobile-action-btn" onClick={handleClick}>
      <div
        className="abm-mobile-action-icon"
        style={{
          background: c.bg,
          color: c.color,
          boxShadow: c.shadow
        }}
      >
        <div style={{ zIndex: 3, position: 'relative' }}>
          {icon}
          {page === "buy-product" && cartCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: "-6px",
                right: "-6px",
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: "#f97316",
                color: "white",
                fontSize: "10px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid white"
              }}
            >
              {cartCount}
            </span>
          )}
        </div>
      </div>
      <span className="abm-mobile-action-label">
        {label.split("\n").map((l, i) => (
          <span key={i} style={{ display: "block" }}>{l}</span>
        ))}
      </span>
    </button>
  );
};

/* ═══════════════════════════════════════════════
   MOBILE HOME DASHBOARD (Coral FAB)
   Shows ONLY the teal header + dashboard content
   NO action cards
═══════════════════════════════════════════════ */
const MobileHomeDashboard = ({ user, navigate_, initials, getDashboard, handleCopy, copySuccess }) => (
  <>
    <div className="abm-mobile-header">
      <div className="abm-mobile-topbar">
        <div className="abm-mobile-logo">
          <img
            src={logo}
            alt="ABM Group"
            style={{ width: "70px", height: "70px", borderRadius: "8px", background: "#05F1FF" }}
          />
        </div>
        <div className="abm-mobile-bal-label" style={{ textAlign: "right" }}>
          <div>Everyday products, daily income</div>
          <div>your progress today, your family’s security tomorrow.</div>
        </div>
      </div>

      <div className="abm-mobile-balance-row">
        <div className="abm-mobile-balance-pill">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "8px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ flexShrink: 0 }}>
              {user.profilePic ? (
                <img
                  src={user.profilePic}
                  alt="Profile"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "20%",
                    background: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid rgba(255,255,255,0.3)",
                    fontSize: "18px",
                  }}
                >
                  {initials}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "16px", fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>Welcome back</div>
                  <div className="abm-mobile-bal-amount" style={{ textAlign: "left", marginTop: "4px" }}>
                    {user.name}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 0 }}>
                  {/* <div className="abm-mobile-userid">ID: {user.uniqueId}</div> */}
                  {/* <div className="abm-mobile-userid">SP: {user.sponsorId}</div> */}
                </div>
              </div>
              <div className="abm-mobile-username">ID: {user.uniqueId}</div>
              <div className="abm-mobile-username">Mobile no. {user.mobile}</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 16px", marginTop: "12px", paddingBottom: "16px" }}>
        <button
          onClick={handleCopy}
          className={`abm-copy-btn ${copySuccess ? "copied" : ""}`}
          style={{ width: "100%", justifyContent: "center" }}
        >
          {copySuccess ? <FiCheck size={14} /> : <FiCopy size={14} />}
          {copySuccess ? "Copied!" : "Copy Referral Link"}
        </button>
      </div>
    </div>
    <div className="abm-mobile-page-content">{getDashboard()}</div>
  </>
);

/* ═══════════════════════════════════════════════
   MOBILE ACCOUNT PAGE (Account tab)
   Shows ONLY teal header + action cards
   NO getDashboard() call
═══════════════════════════════════════════════ */
const MobileAccountPage = ({
  user,
  navigate_,
  handleCopy,
  copySuccess,
  uniqueIdReferralUrl,
  initials,
  mobileRow1,
  mobileRow2,
  mobileRow3,
  mobileRow4,
  getCartCount,
}) => {

  return (
    <>
      <div className="abm-mobile-header">
        <div className="abm-mobile-topbar">
          <div className="abm-mobile-logo">
            <img
              src={logo}
              alt="ABM Group"
              style={{ width: "70px", height: "70px", borderRadius: "9px" }}
            />
          </div>
          <div className="abm-mobile-bal-label" style={{ textAlign: "right" }}>
            <div>Everyday products, daily income</div>
            <div>your progress today, your family’s security tomorrow.</div>
          </div>
        </div>

        <div className="abm-mobile-balance-row">
          <div className="abm-mobile-balance-pill">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "8px",
                flexWrap: "wrap",
              }}
            >
              <div style={{ flexShrink: 0 }}>
                {user.profilePic ? (
                  <img
                    src={user.profilePic}
                    alt="Profile"
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid rgba(255,255,255,0.3)",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "20%",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid rgba(255,255,255,0.3)",
                      fontSize: "18px",
                    }}
                  >
                    {initials}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "16px", fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>Welcome back</div>
                    <div className="abm-mobile-bal-amount" style={{ textAlign: "left", marginTop: "4px" }}>
                      {user.name}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 0 }}>
                    {/* <div className="abm-mobile-userid">ID: {user.uniqueId}</div> */}
                    {/* <div className="abm-mobile-userid">SP: {user.sponsorId}</div> */}
                  </div>
                </div>
                <div className="abm-mobile-username">ID: {user.uniqueId}</div>
                <div className="abm-mobile-username">Mobile no. {user.mobile}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Copy referral link */}
        <div style={{ padding: "0 16px", marginTop: "12px", paddingBottom: "16px" }}>
          <button
            onClick={handleCopy}
            className={`abm-copy-btn ${copySuccess ? "copied" : ""}`}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {copySuccess ? <FiCheck size={14} /> : <FiCopy size={14} />}
            {copySuccess ? "Copied!" : "Copy Referral Link"}
          </button>
        </div>
      </div>

      {/* Action cards — NO dashboard below */}
      <div style={{ padding: "0 0 calc(var(--mobile-bottom-nav-h) + 16px) 0" }}>
        {/* More Services Card */}
        <div className="abm-mobile-action-card" style={{ marginBottom: "12px" }}>
          <div className="abm-action-section-label">More Services</div>
          <div className="abm-mobile-actions">
            {mobileRow1.map((item) => (
              <ActionBtn key={item.page} {...item} navigate_={navigate_} />
            ))}
          </div>
        </div>

        {/* LBBP Services Card */}
        <div className="abm-mobile-action-card" style={{ marginBottom: "12px" }}>
          <div className="abm-action-section-label">LBBP Services</div>
          <div className="abm-mobile-actions">
            {mobileRow2.map((item) => (
              <ActionBtn key={item.page} {...item} navigate_={navigate_} />
            ))}
          </div>
        </div>

        {/* PU&AP Services Card */}
        <div className="abm-mobile-action-card" style={{ marginBottom: "12px" }}>
          <div className="abm-action-section-label">PU&AP Services</div>
          <div className="abm-mobile-actions">
            {mobileRow3.map((item) => (
              <ActionBtn
                key={item.page}
                {...item}
                navigate_={navigate_}
              />
            ))}
          </div>
        </div>

        {/* Product Purchase Card */}
        <div className="abm-mobile-action-card" style={{ marginBottom: "12px" }}>
          <div className="abm-action-section-label">Product Purchase</div>
          <div className="abm-mobile-actions">
            {mobileRow4.map((item) => (
              <ActionBtn
                key={item.page}
                {...item}
                navigate_={navigate_}
                cartCount={item.page === "buy-product" ? getCartCount() : 0}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);
  const [copySuccess, setCopySuccess] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    moreServices: false,
    lbbpServices: false,
    puapServices: false,
    productPurchase: false,
    account: false
  });
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);

  // Check for page query parameter
  const queryParams = new URLSearchParams(location.search);
  const initialPage = queryParams.get("page") || "dashboard";
  const [activePage, setActivePage] = useState(initialPage);
  const [dashboardMode, setDashboardMode] = useState(() =>
    localStorage.getItem("dashboardMode")
  );

  // Update activePage if query parameter changes (smart monitoring)
  useEffect(() => {
    // Skip if this was triggered by our own navigation
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    const params = new URLSearchParams(location.search);
    const pageParam = params.get("page");

    // Only update if different from current state
    if (pageParam && pageParam !== activePage) {
      setActivePage(pageParam);
    } else if (!pageParam && activePage !== "dashboard") {
      // If no page parameter but not on dashboard, go to dashboard
      setActivePage("dashboard");
    }
  }, [location.search]);
  // alert(location.search);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/dashboard/member`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setUser(data.user);
          if (data.user.isMobileStore) {
            localStorage.setItem("dashboardMode", "lbbp");
            setDashboardMode("lbbp");
          } else {
            localStorage.removeItem("dashboardMode");
            setDashboardMode(null);
          }
        }
      } catch {
        // Don't navigate, just leave user as null
      }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 769;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Handle back button to prevent navigation to login page
  useEffect(() => {
    const handlePopState = (event) => {
      // If user is logged in and inside dashboard, prevent back to login
      if (user && activePage !== "dashboard" && activePage !== "home-dashboard") {
        event.preventDefault();
        // Navigate to dashboard home
        setActivePage("dashboard");
        // Update URL without triggering navigation
        window.history.pushState(null, "", window.location.pathname);
      }
    };

    // Push initial state to enable popstate detection
    window.history.pushState(null, "", window.location.pathname);

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [user, activePage]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("dashboardMode");
    window.location.reload();
  };

  const navigate_ = (page) => {
    if (page === "mobile_store") {
      selectDashboardMode("mobile-store");
      return;
    }
    // Check if page requires login
    const pagesRequiringLogin = [
      "team", "wallet", "transactions", "complete-kyc", "admin",
      "manual-payment", "level-income", "buy-share", "share-purchases",
      "royalty-history", "direct-income", "matrix-income",
      "generation-income", "active-member-income", "reward-tour-income",
      "car-bonus-income", "royalty-income", "profile", "account",
      "home-dashboard", "buy-product", "welcome-letter", "id-card", "royalty-eligibility"
    ];

    if (!user && pagesRequiringLogin.includes(page)) {
      setShowLoginModal(true);
    } else {
      isNavigatingRef.current = true; // Mark as user-initiated
      setActivePage(page);
      // Update URL when state changes
      const newUrl = page === "dashboard" ? "/dashboard" : `/dashboard?page=${page}`;
      navigate(newUrl, { replace: true });
      if (isMobile) setSidebarOpen(false);
    }
  };

  const referralBaseUrl =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
      ? `${window.location.protocol}//${window.location.host}`
      : "https://www.abmgroupone.com";

  const uniqueIdReferralUrl = user?.uniqueId
    ? `${referralBaseUrl}/register?${encodeURIComponent(user.uniqueId)}`
    : "";

  const handleCopy = async () => {
    if (!uniqueIdReferralUrl) return;
    try {
      await navigator.clipboard.writeText(uniqueIdReferralUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2200);
    } catch { }
  };

  const openPdf = (fileName) => {
    window.open(`/${fileName}`, "_blank", "noopener,noreferrer");
    setShowPdfModal(false);
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getDashboard = () => {
    switch (user?.userType) {
      case "office_manager": return <OfficeManagerDashboard user={user} />;
      case "pickup_center": return <MPCDashboard user={user} />;
      default:
        // For Mobile Store users, show selected dashboard
        if (user?.isMobileStore) {
          if (activePage === "mobile_store") {
            if (dashboardMode !== "mobile-store") {
              return <LBBPDashboard user={user} setActivePage={setActivePage} />;
            }
            return <MobileStoreDashboard />;
          }
          // Otherwise show member dashboard based on partnership status
          if (user?.isShareActive && !user?.isProductActive)
            return <LBBPDashboard user={user} setActivePage={setActivePage} />;
          if (user?.isProductActive && !user?.isShareActive)
            return <PUAPDashboard user={user} />;
          if (user?.isShareActive && user?.isProductActive)
            return <DualDashboard user={user} setActivePage={setActivePage} />;
          return <MemberDashboard user={user} />;
        }
        // Regular member dashboard logic
        if (user?.isShareActive && !user?.isProductActive)
          return <LBBPDashboard user={user} setActivePage={setActivePage} />;
        if (user?.isProductActive && !user?.isShareActive)
          return <PUAPDashboard user={user} />;
        if (user?.isShareActive && user?.isProductActive)
          return <DualDashboard user={user} setActivePage={setActivePage} />;
        return <MemberDashboard user={user} />;
    }
  };

  const initials = user?.uniqueId?.substring(0, 3) || "USR";

  const selectDashboardMode = (mode) => {
    localStorage.setItem("dashboardMode", mode);
    setDashboardMode(mode);
    if (mode === "mobile-store") {
      navigate("/mobile-store-dashboard");
    } else {
      navigate("/dashboard");
      setActivePage("dashboard");
    }
  };

  /* ─────────────────────────────────────────────
     Mobile action rows
  ───────────────────────────────────────────── */
  const mobileRow1 = [
    { page: "team", label: "My Team", icon: <Users size={32} style={{ color: "#FFF" }} /> },
    { page: "wallet", label: "Wallet", icon: <WalletIcon size={32} style={{ color: "#FFF" }} /> },
     { page: "welcome-letter", label: "Welcome\nLetter", icon: <FiFileText size={32} style={{ color: "#FFF" }} /> },
    { page: "id-card", label: "Member\nID Card", icon: <FiUser size={32} style={{ color: "#FFF" }} /> },
    { page: "transactions", label: "Transaction\nHistory", icon: <Receipt size={32} style={{ color: "#FFF" }} /> },
    { page: "wallet", label: "Transfer\nMember E Wallet", icon: <FiSend size={32} style={{ color: "#FFF" }} /> },
    {
      page: "whatsapp",
      label: "WhatsApp\nSupport",
      icon: <FaWhatsapp size={32} style={{ color: "#FFF" }} />,
      onClick: () => window.open("https://wa.me/918887355992", "_blank", "noopener,noreferrer"),
    },
    {
      page: "pdf-brochure",
      label: "PDF\nBrochure",
      icon: <FiFileText size={32} style={{ color: "#FFF" }} />,
      onClick: () => setShowPdfModal(true),
    },
    ...(user?.kycStatus === "pending" || user?.kycStatus === "rejected"
      ? [{ page: "complete-kyc", label: "KYC", icon: <Shield size={32} style={{ color: "#FFF" }} /> }]
      : []),
    ...(user?.isMobileStore
      ? [{ page: "mobile_store", label: "Mobile\nStore", icon: <FiShoppingBag size={32} style={{ color: "#FFF" }} /> }]
      : []),
    // ...(user?.role === "admin"
    //   ? [{ page: "admin", label: "Admin Panel", icon: <Gavel size={32} style={{ color: "#FFF" }} /> }]
    //   : []),
  ];

  const mobileRow2 = [
    { page: "manual-payment", label: "Add Money", icon: <HandCoins size={32} style={{ color: "#FFF" }} /> },
    { page: "level-income", label: "CCP Level\nHistory", icon: <TrendingUp size={32} style={{ color: "#FFF" }} /> },
    { page: "buy-share", label: "GCP Loan Pay", icon: <CreditCard size={32} style={{ color: "#FFF" }} /> },
    { page: "share-purchases", label: "GCP Loan Pay\nHistory", icon: <FileSpreadsheet size={32} style={{ color: "#FFF" }} /> },
    { page: "royalty-history", label: "GIP Royalty\nHistory", icon: <Crown size={32} style={{ color: "#FFF" }} /> },
    { page: "royalty-eligibility", label: "Royalty\nEligibility", icon: <FiAward size={32} style={{ color: "#FFF" }} /> },
   
  ];

  const mobileRow3 = [
    { page: "direct-income", label: "Direct\nIncome", icon: <HandCoins size={32} style={{ color: "#FFF" }} /> },
    { page: "matrix-income", label: "Matrix\nIncome", icon: <Layers size={32} style={{ color: "#FFF" }} /> },
    { page: "generation-income", label: "Generation\nIncome", icon: <Users2 size={32} style={{ color: "#FFF" }} /> },
    { page: "active-member-income", label: "Active Member\nIncome", icon: <UserCheck size={32} style={{ color: "#FFF" }} /> },
    { page: "reward-tour-income", label: "Reward Tour\nBonus", icon: <PlaneTakeoff size={32} style={{ color: "#FFF" }} /> },
    { page: "car-bonus-income", label: "Car Bonus\nIncome", icon: <Car size={32} style={{ color: "#FFF" }} /> },
    { page: "royalty-income", label: "Royalty\nIncome", icon: <Crown size={32} style={{ color: "#FFF" }} /> },
    { page: "direct-team", label: "Direct\nTeam", icon: <FiUsers size={32} style={{ color: "#FFF" }} /> },
    { page: "matrix-team", label: "Matrix\nTeam", icon: <Layers size={32} style={{ color: "#FFF" }} /> },
    { page: "generation-team", label: "Generation\nTeam", icon: <Users2 size={32} style={{ color: "#FFF" }} /> },
  ];

  const mobileRow4 = [
    { page: "self-purchase", label: "Self\nPurchase", icon: <FiShoppingBag size={32} style={{ color: "#FFF" }} /> },
    { page: "my-orders", label: "My\nOrders", icon: <FiFileText size={32} style={{ color: "#FFF" }} /> },
    { page: "purchase-volume", label: "Purchase\nVolume", icon: <FiTrendingUp size={32} style={{ color: "#FFF" }} /> },
    { page: "buy-product", label: "Cart", icon: <FiShoppingCart size={32} style={{ color: "#FFF" }} /> },
  ];

  const renderPage = () => {
    // If not logged in, show ProductHome except for "my-shop"
    if (!user && activePage !== "my-shop") {
      return <ProductHome user={user} />;
    }

    const puapIncomeTitles = {
      "direct-income": { title: "Direct Income", description: "Commission earned from direct referrals' product purchases" },
      "matrix-income": { title: "Matrix Income", description: "Commission earned from matrix levels in your team" },
      "generation-income": { title: "Generation Income", description: "Commission earned from generation levels in your team" },
      "active-member-income": { title: "Active Member Reward", description: "Reward for maintaining active membership status" },
      "reward-tour-income": { title: "Reward Tour Bonus", description: "Bonus for achieving reward tour ranks" },
      "car-bonus-income": { title: "Car Bonus Income", description: "Bonus for achieving car bonus ranks" },
      "royalty-income": { title: "Royalty Income", description: "Royalty bonus for achieving royalty ranks" },
    };

    switch (activePage) {
      case "wallet": return <Wallet user={user} />;
      case "team": return <Team user={user} />;
      case "level-income": return <LevelIncomeReport user={user} />;
      case "admin": return <AdminDashboard user={user} />;
      case "royalty-history": return <RoyaltyHistory user={user} />;
      case "royalty-eligibility": return <RoyaltyEligibility user={user} />;
      case "buy-share": return <BuyShare user={user} />;
      case "buy-product": return <BuyProduct user={user} />;
      case "share-purchases": return <SharePurchases user={user} />;
      case "profile": return <UserProfilePage user={user} />;
      case "welcome-letter": return <WelcomeLetterPage user={user} />;
      case "id-card": return <IdCardPage user={user} />;
      case "complete-kyc": return <RegisterKYCDashboard />;
      case "select-partner": return <RegisterPartnerDashboard />;
      case "manual-payment": return <ManualPayment />;
      case "transactions": return <UserTransactions />;
      case "my-shop": return <CategoryProductsPage user={user} />;
      case "mobile_store":
        return dashboardMode === "mobile-store"
          ? <MobileStoreDashboard />
          : getDashboard();
      case "direct-income": return <PUAPIncomePage user={user} incomeType="direct-income" title={puapIncomeTitles["direct-income"].title} description={puapIncomeTitles["direct-income"].description} />;
      case "matrix-income": return <PUAPIncomePage user={user} incomeType="matrix-income" title={puapIncomeTitles["matrix-income"].title} description={puapIncomeTitles["matrix-income"].description} />;
      case "generation-income": return <PUAPIncomePage user={user} incomeType="generation-income" title={puapIncomeTitles["generation-income"].title} description={puapIncomeTitles["generation-income"].description} />;
      case "active-member-income": return <PUAPIncomePage user={user} incomeType="active-member-income" title={puapIncomeTitles["active-member-income"].title} description={puapIncomeTitles["active-member-income"].description} />;
      case "reward-tour-income": return <PUAPIncomePage user={user} incomeType="reward-tour-income" title={puapIncomeTitles["reward-tour-income"].title} description={puapIncomeTitles["reward-tour-income"].description} />;
      case "car-bonus-income": return <PUAPIncomePage user={user} incomeType="car-bonus-income" title={puapIncomeTitles["car-bonus-income"].title} description={puapIncomeTitles["car-bonus-income"].description} />;
      case "royalty-income": return <PUAPIncomePage user={user} incomeType="royalty-income" title={puapIncomeTitles["royalty-income"].title} description={puapIncomeTitles["royalty-income"].description} />;
      case "direct-team": return <DirectTeam user={user} />;
      case "matrix-team": return <MatrixTeam user={user} />;
      case "generation-team": return <GenerationTeam user={user} />;
      case "self-purchase": return <SelfPurchase user={user} />;
      case "my-orders": return <MyOrders user={user} />;
      case "purchase-volume": return <PurchaseVolume user={user} />;

      /* ── Coral FAB: dashboard ONLY, no action cards ── */
      case "home-dashboard":
        return (
          <MobileHomeDashboard
            user={user}
            navigate_={navigate_}
            initials={initials}
            getDashboard={getDashboard}
            handleCopy={handleCopy}
            copySuccess={copySuccess}
          />
        );

      /* ── Account tab: action cards ONLY, no dashboard ── */
      case "account":
        return (
          <MobileAccountPage
            user={user}
            navigate_={navigate_}
            handleCopy={handleCopy}
            copySuccess={copySuccess}
            uniqueIdReferralUrl={uniqueIdReferralUrl}
            initials={initials}
            mobileRow1={mobileRow1}
            mobileRow2={mobileRow2}
            mobileRow3={mobileRow3}
            mobileRow4={mobileRow4}
            getCartCount={getCartCount}
          />
        );

      default:
        if (isMobile) return <ProductHome user={user} />;
        return getDashboard();
    }
  };

  const meta = PAGE_META[activePage] || PAGE_META.dashboard;

  const sidebarClass = [
    "abm-sidebar",
    !isMobile && sidebarCollapsed ? "collapsed" : "",
    isMobile ? (sidebarOpen ? "mobile-visible" : "mobile-hidden") : "",
  ].join(" ");

  const mainClass = [
    "abm-main",
    !isMobile && sidebarCollapsed ? "sidebar-collapsed" : "",
    isMobile ? "mobile" : "",
  ].join(" ");

  if (loading) return (
    <div className="abm-loading">
      <style>{styles}</style>
      <div>
        <div className="abm-spinner" />
        <p className="abm-loading-text">Loading your dashboard…</p>
      </div>
    </div>
  );

  return (
    <div className="abm-root">
      <style>{styles}</style>
      <div className="abm-shell">

        <div
          className={`abm-overlay ${!isMobile && sidebarOpen ? "visible" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* ════════════════════════════════
            DESKTOP SIDEBAR
        ════════════════════════════════ */}
        <div className={sidebarClass}>
          <div className="abm-logo">
            <div className="abm-logo-mark">{initials}</div>
            <span className="abm-logo-text">ABM Group</span>
          </div>
          {user && (
            <div className="abm-user-card">
              <div className="abm-user-card-inner">
                <div className="abm-avatar">{initials}</div>
                <div className="abm-user-info">
                  <div className="abm-user-name">{user.name}</div>
                  <div className="abm-user-id">ID: {user.uniqueId}</div>
                  <div className="abm-user-id">SP: {user.sponsorId}</div>
                </div>
              </div>
            </div>
          )}
          <nav className="abm-nav">
            <CollapsibleNavSection
              label="More Services"
              collapsed={collapsedSections.moreServices}
              onToggle={() => toggleSection("moreServices")}
              sidebarCollapsed={sidebarCollapsed}
            >
              <NavItem icon={<FiHome />} label="Dashboard" active={activePage === "dashboard"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("dashboard")} />
              {user && (
                <>
                  <NavItem icon={<FiDollarSign />} label="Wallet" active={activePage === "wallet"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("wallet")} />
                  <NavItem icon={<FiUsers />} label="Team" active={activePage === "team"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("team")} />
                  <NavItem icon={<FiFileText />} label="Transactions" active={activePage === "transactions"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("transactions")} />
                  <NavItem icon={<FiFileText />} label="PDF Brochure" sidebarOpen={!sidebarCollapsed} onClick={() => setShowPdfModal(true)} />
                  <NavItem icon={<FiShoppingBag />} label="My Shop" active={activePage === "my-shop"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("my-shop")} />
                  {user.isMobileStore && (
                    <NavItem icon={<FiShoppingBag />} label="Mobile Store" active={activePage === "mobile_store"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("mobile_store")} />
                  )}
                </>
              )}
            </CollapsibleNavSection>

            {user && (
              <CollapsibleNavSection
                label="LBBP Services"
                collapsed={collapsedSections.lbbpServices}
                onToggle={() => toggleSection("lbbpServices")}
                sidebarCollapsed={sidebarCollapsed}
              >
                <NavItem icon={<FiSend />} label="Add Money" active={activePage === "manual-payment"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("manual-payment")} />
                <NavItem icon={<FiTrendingUp />} label="CCP Level Income" active={activePage === "level-income"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("level-income")} />
                <NavItem icon={<FiTrendingUp />} label="GCP Loan Buy" active={activePage === "buy-share"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("buy-share")} />
                <NavItem icon={<FiAward />} label="GIP Royalty History" active={activePage === "royalty-history"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("royalty-history")} />
                <NavItem icon={<FiAward />} label="Royalty Eligibility" active={activePage === "royalty-eligibility"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("royalty-eligibility")} />
                <NavItem icon={<FiBriefcase />} label="My GCP Loan History" active={activePage === "share-purchases"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("share-purchases")} />
              </CollapsibleNavSection>
            )}

            {user && (
              <CollapsibleNavSection
                label="PU&AP Services"
                collapsed={collapsedSections.puapServices}
                onToggle={() => toggleSection("puapServices")}
                sidebarCollapsed={sidebarCollapsed}
              >
                <NavItem icon={<HandCoins />} label="Direct Income" active={activePage === "direct-income"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("direct-income")} />
                <NavItem icon={<Layers />} label="Matrix Income" active={activePage === "matrix-income"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("matrix-income")} />
                <NavItem icon={<Users2 />} label="Generation Income" active={activePage === "generation-income"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("generation-income")} />
                <NavItem icon={<UserCheck />} label="Active Member Income" active={activePage === "active-member-income"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("active-member-income")} />
                <NavItem icon={<PlaneTakeoff />} label="Reward Tour Bonus" active={activePage === "reward-tour-income"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("reward-tour-income")} />
                <NavItem icon={<Car />} label="Car Bonus Income" active={activePage === "car-bonus-income"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("car-bonus-income")} />
                <NavItem icon={<Crown />} label="Royalty Income" active={activePage === "royalty-income"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("royalty-income")} />
                <NavItem icon={<FiUsers />} label="Direct Team" active={activePage === "direct-team"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("direct-team")} />
                <NavItem icon={<Layers />} label="Matrix Team" active={activePage === "matrix-team"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("matrix-team")} />
                <NavItem icon={<Users2 />} label="Generation Team" active={activePage === "generation-team"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("generation-team")} />
              </CollapsibleNavSection>
            )}

            {user && (
              <CollapsibleNavSection
                label="Product Purchase"
                collapsed={collapsedSections.productPurchase}
                onToggle={() => toggleSection("productPurchase")}
                sidebarCollapsed={sidebarCollapsed}
              >
                <NavItem icon={<FiShoppingBag />} label="Self Purchase" active={activePage === "self-purchase"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("self-purchase")} />
                <NavItem icon={<FiFileText />} label="My Orders" active={activePage === "my-orders"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("my-orders")} />
                <NavItem icon={<FiTrendingUp />} label="Purchase Volume" active={activePage === "purchase-volume"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("purchase-volume")} />
                <NavItem icon={<FiShoppingCart />} label="Cart" active={activePage === "buy-product"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("buy-product")} />
              </CollapsibleNavSection>
            )}

            {user && (
              <CollapsibleNavSection
                label="Account"
                collapsed={collapsedSections.account}
                onToggle={() => toggleSection("account")}
                sidebarCollapsed={sidebarCollapsed}
              >
                {(user.kycStatus === "pending" || user.kycStatus === "rejected") && (
                  <NavItem icon={<FiShield />} label="Complete KYC" active={activePage === "complete-kyc"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("complete-kyc")} />
                )}
                <NavItem icon={<FiUser />} label="Profile" active={activePage === "profile"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("profile")} />
                <NavItem icon={<FiFileText />} label="Welcome Letter" active={activePage === "welcome-letter"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("welcome-letter")} />
                <NavItem icon={<FiUser />} label="Member ID Card" active={activePage === "id-card"} sidebarOpen={!sidebarCollapsed} onClick={() => navigate_("id-card")} />
              </CollapsibleNavSection>
            )}
          </nav>
          {user && (
            <>
              <button className="abm-collapse-btn" onClick={() => setSidebarCollapsed(v => !v)}>
                {sidebarCollapsed ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />}
                <span className="abm-collapse-label">Collapse</span>
              </button>
              <button className="abm-logout-btn" onClick={handleLogout}>
                <FiLogOut size={16} style={{ flexShrink: 0 }} />
                <span className="abm-logout-label">Logout</span>
              </button>
            </>
          )}
        </div>

        {/* ════════════════════════════════
            MAIN CONTENT
        ════════════════════════════════ */}
        <div className={mainClass}>

          {/* Desktop header */}
          <header className="abm-header">
            <div className="abm-header-title">
              <div className="abm-header-breadcrumb">{meta.breadcrumb}</div>
              <div className="abm-header-page">{meta.label}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {user && (
                <button
                  onClick={() => navigate_("buy-product")}
                  className="abm-header-user"
                  style={{ position: "relative", cursor: "pointer" }}
                >
                  <FiShoppingCart size={20} style={{ color: "#475569" }} />
                  {getCartCount() > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "-6px",
                        right: "-6px",
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        backgroundColor: "#f59e0b",
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid white"
                      }}
                    >
                      {getCartCount()}
                    </span>
                  )}
                </button>
              )}
              {user && (
                <div className="abm-header-user">
                  <div className="abm-header-avatar">{initials}</div>
                  <div className="abm-header-user-info">
                    <div className="abm-header-user-name">{user.name}</div>
                    <div className="abm-header-user-id">{user.uniqueId}</div>
                  </div>
                </div>
              )}
            </div>
          </header>

          <div className="abm-content">

            {/* Desktop referral card */}
            {user?.uniqueId && (
              <div className="abm-ref-card">
                <div className="abm-ref-icon"><FiCopy size={20} /></div>
                <div className="abm-ref-body">
                  <div className="abm-ref-label">Your Referral Link</div>
                  <div className="abm-ref-id">{user.uniqueId}</div>
                  <div className="abm-ref-name">{user.name}</div>
                  <div className="abm-ref-link">{uniqueIdReferralUrl}</div>
                </div>
                <button
                  onClick={handleCopy}
                  className={`abm-copy-btn ${copySuccess ? "copied" : ""}`}
                >
                  {copySuccess ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  {copySuccess ? "Copied!" : "Copy Link"}
                </button>
              </div>
            )}

            {/* Mobile home (default dashboard tab) — ProductHome only */}
            {isMobile && activePage === "dashboard" ? (
              <div className="abm-mobile-page-content">
                <ProductHome user={user} />
              </div>
            ) : (
              <div className={isMobile ? "abm-mobile-page-content" : ""}>
                {renderPage()}
              </div>
            )}
          </div>
        </div>

        {/* ════════════════════════════════
            MOBILE BOTTOM NAV
        ════════════════════════════════ */}
        <nav className="abm-mobile-bottom-nav">

          {/* Home */}
          <button
            className={`abm-mobile-nav-item ${activePage === "dashboard" ? "active" : ""}`}
            onClick={() => navigate_("dashboard")}
          >
            <span className="abm-mobile-nav-icon"><FiHome size={22} /></span>
            <span className="abm-mobile-nav-label">Home</span>
          </button>

          {/* My Shop */}
          <button
            className={`abm-mobile-nav-item ${activePage === "my-shop" ? "active" : ""}`}
            onClick={() => navigate_("my-shop")}
          >
            <span className="abm-mobile-nav-icon"><FiShoppingBag size={22} /></span>
            <span className="abm-mobile-nav-label">My Shop</span>
          </button>

          <button
            className={`abm-mobile-nav-item ${activePage === "home-dashboard" ? "active" : ""}`}
            onClick={() => navigate_("home-dashboard")}
          >
            <span className="abm-mobile-nav-icon"><FiUsers size={22} /></span>
            <span className="abm-mobile-nav-label">Business</span>
          </button>

          {/* Coral FAB → home-dashboard (dashboard only, no action cards) */}
          {/* <button
            className="abm-mobile-nav-fab"
            onClick={() => navigate_("home-dashboard")}
            aria-label="Go to dashboard"
          >
            <FiHome size={24} color="#fff" />
          </button> */}

          {/* Profile */}
          <>
            <button
              className={`abm-mobile-nav-item ${activePage === "profile" ? "active" : ""}`}
              onClick={() => navigate_("profile")}
            >
              <span className="abm-mobile-nav-icon"><FiUser size={22} /></span>
              <span className="abm-mobile-nav-label">Profile</span>
            </button>

            {/* Account tab → action cards only, no dashboard */}
            <button
              className={`abm-mobile-nav-item ${activePage === "account" ? "active" : ""}`}
              onClick={() => navigate_("account")}
            >
              <span className="abm-mobile-nav-icon"><FiSettings size={22} /></span>
              <span className="abm-mobile-nav-label">Account</span>
            </button>
          </>

        </nav>

        {showPdfModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: "20px"
            }}
            onClick={() => setShowPdfModal(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "24px",
                maxWidth: "420px",
                width: "100%",
                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", marginBottom: "8px" }}>
                Brochure PDFs
              </h3>
              <p style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.5, marginBottom: "16px" }}>
                Choose the brochure language to view or download.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <button
                  style={{
                    padding: "12px 16px",
                    borderRadius: "10px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
                    color: "white",
                    border: "none"
                  }}
                  onClick={() => openPdf("ABMGROUPONE_HINDI.pdf")}
                >
                  Hindi PDF
                </button>
                <button
                  style={{
                    padding: "12px 16px",
                    borderRadius: "10px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
                    color: "white",
                    border: "none"
                  }}
                  onClick={() => openPdf("ABMGROUPONE_ENGLISH.pdf")}
                >
                  English PDF
                </button>
              </div>
              <button
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent",
                  color: "#64748b",
                  border: "none",
                  marginTop: "10px"
                }}
                onClick={() => setShowPdfModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Login Modal */}
        {showLoginModal && (<div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
          onClick={() => setShowLoginModal(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "32px",
              margin: "20px",
              maxWidth: "400px",
              width: "100%",
              textAlign: "center",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔐</div>
            <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", marginBottom: "8px" }}>
              Login Required
            </h3>
            <p style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.5, marginBottom: "24px" }}>
              Please log in to access this feature.
            </p>
            <button
              style={{
                width: "100%",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Montserrat', sans-serif",
                background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                color: "white",
                border: "none"
              }}
              onClick={() => {
                setShowLoginModal(false);
                navigate("/login");
              }}
            >
              Go to Login
            </button>
            <button
              style={{
                width: "100%",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Montserrat', sans-serif",
                background: "transparent",
                color: "#64748b",
                border: "none",
                marginTop: "8px"
              }}
              onClick={() => setShowLoginModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
        )}

      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   DUAL DASHBOARD (unchanged)
═══════════════════════════════════════════════ */
const DualDashboard = ({ user, setActivePage }) => {
  const [tab, setTab] = useState("member");
  return (
    <div>
      <div className="abm-tab-row">
        {[
          { id: "member", label: "Member View" },
          { id: "lbbp", label: "LBBP Dashboard" },
          { id: "puap", label: "PU&AP Dashboard" },
        ].map(t => (
          <button
            key={t.id}
            className={`abm-tab-btn ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="abm-tab-content">
        {tab === "member" && <MemberDashboard user={user} />}
        {tab === "lbbp" && <LBBPDashboard user={user} />}
        {tab === "puap" && <PUAPDashboard user={user} />}
      </div>
    </div>
  );
};

export default Dashboard;